import type { ILanguageResources, ITranslationModule } from '../../../../interfaces';
import { interpolate } from '../../../../global';

// Logical area: FormInputView General Strings
class Id {
  private constructor() {}

  public static readonly prefix = 'UI.webmodule-common.other.ui.templateresult.FormInputView.';

  public static readonly supportedFileTypes = `${Id.prefix}supportedFileTypes`;
  public static readonly first = `${Id.prefix}first`;
  public static readonly prev = `${Id.prefix}prev`;
  public static readonly next = `${Id.prefix}next`;
  public static readonly last = `${Id.prefix}last`;
  public static readonly uploadedFileThumbnailAlt = `${Id.prefix}uploadedFileThumbnailAlt`;
  public static readonly customRequestThumbnailAlt = `${Id.prefix}customRequestThumbnailAlt`;
  public static readonly thumbnailLabel = `${Id.prefix}thumbnailLabel`;
  public static readonly chooseImage = `${Id.prefix}chooseImage`;
  public static readonly pasteImage = `${Id.prefix}pasteImage`;
  public static readonly clipboardHasNoImage = `${Id.prefix}clipboardHasNoImage`;
  public static readonly clipboardUnavailable = `${Id.prefix}clipboardUnavailable`;
  public static readonly pasteHint = `${Id.prefix}pasteHint`;
  public static readonly pasteNotAnImage = `${Id.prefix}pasteNotAnImage`;
}

export class LangFormInputView {
  private constructor() {}

  public static supportedFileTypes(acceptedFiles: string): string {
    return interpolate(Id.supportedFileTypes, { acceptedFiles });
  }

  public static first(): string {
    return interpolate(Id.first);
  }

  public static prev(): string {
    return interpolate(Id.prev);
  }

  public static next(): string {
    return interpolate(Id.next);
  }

  public static last(): string {
    return interpolate(Id.last);
  }

  public static uploadedFileThumbnailAlt(): string {
    return interpolate(Id.uploadedFileThumbnailAlt);
  }

  public static customRequestThumbnailAlt(): string {
    return interpolate(Id.customRequestThumbnailAlt);
  }

  public static thumbnailLabel(): string {
    return interpolate(Id.thumbnailLabel);
  }

  public static chooseImage(): string {
    return interpolate(Id.chooseImage);
  }

  public static pasteImage(): string {
    return interpolate(Id.pasteImage);
  }

  public static clipboardHasNoImage(): string {
    return interpolate(Id.clipboardHasNoImage);
  }

  public static clipboardUnavailable(): string {
    return interpolate(Id.clipboardUnavailable);
  }

  public static pasteHint(): string {
    return interpolate(Id.pasteHint);
  }

  public static pasteNotAnImage(acceptedFiles: string): string {
    return interpolate(Id.pasteNotAnImage, { acceptedFiles });
  }
}

export class FormInputViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    // General
    register.add(
      Id.supportedFileTypes,
      'Currently supported file types are {{acceptedFiles}}',
      'Supported file types message'
    );
    register.add(Id.first, 'First', 'Paginator: first page');
    register.add(Id.prev, 'Prev', 'Paginator: previous page');
    register.add(Id.next, 'Next', 'Paginator: next page');
    register.add(Id.last, 'Last', 'Paginator: last page');
    register.add(
      Id.uploadedFileThumbnailAlt,
      'Thumbnail for the uploaded file',
      'Alt text for uploaded file thumbnail'
    );
    register.add(Id.customRequestThumbnailAlt, 'Custom Request Thumbnail', 'Alt text for custom request thumbnail');
    register.add(Id.thumbnailLabel, 'Thumbnail', 'Label for thumbnail image');
    register.add(Id.chooseImage, 'Choose file', 'Button: open the file picker to choose an image');
    register.add(Id.pasteImage, 'Paste', 'Button: paste an image from the clipboard');
    register.add(
      Id.clipboardHasNoImage,
      'No image could be read from the clipboard. If you copied an image file rather than the image itself, ' +
        'hover over or click the thumbnail and press Ctrl+V. This is a browser security restriction: a page is ' +
        'never allowed to read a copied file off the clipboard, so only Ctrl+V can hand it over.',
      'Message when the clipboard holds no image the button can read'
    );
    register.add(
      Id.clipboardUnavailable,
      'This browser will not let the page read the clipboard, which is a security restriction rather than a ' +
        'fault. Hover over or click the thumbnail and press Ctrl+V instead, or choose a file.',
      'Message when the clipboard API is unavailable or was denied'
    );
    register.add(Id.pasteHint, 'Press Ctrl+V to paste an image', 'Hint shown on the focused thumbnail drop zone');
    register.add(
      Id.pasteNotAnImage,
      'Nothing on the clipboard could be used as an image here. Supported types are {{acceptedFiles}}.',
      'Message when a paste aimed at the image tile held no usable image'
    );
  }
}
